import { Container, Graphics, Text } from "pixi.js"

import { isMobile } from "../../../../client_util"
import { TextRenderer } from "../text/text_renderer"
import { UI_THEMES } from "./ui_tokens"

/**
 * The prompt over an injured ember survivor: how to get her on her feet.
 *
 * It answers the one question she cannot answer herself. She asks for help in her own words, over
 * and over, for as long as she is lying at the wreck ("Help me! I can't get up.") -- and a player
 * who walks up to her, swings at her, taps her and gets a refusal has still not been told the one
 * thing that works, because "hold the button down on a person" is not a gesture this game has asked
 * for anywhere else. Her interaction LABEL says it ("Hold to help up"), but that label only exists
 * inside a menu she has no reason to open, and it is a menu she deliberately does not have.
 *
 * WHEN it is up is the server's call, not this widget's: it goes up when the last skeleton at the
 * wreck falls, because until then the hold is refused outright and a prompt would be a lie. See
 * Message.rescueHelpHint and RescueDirector.publishHelpHint.
 *
 * One of these is parented to the survivor's world-overlay item (see WorldOverlaySurface), like the
 * countdown dial that replaces it the moment somebody starts holding, so it travels with her for
 * free and needs no per-frame position arithmetic of its own.
 */

// Geometry in world-overlay pixels: that surface is unit-scaled and pre-multiplies world
// coordinates itself, so these are screen pixels at the default zoom -- the same frame the hold
// dial and the role glyph are laid out in.
//
// UNDER THE BODY, not over it. Above her head is taken: it is where her own speech bubble opens,
// and she is speaking the whole time this is up -- the pleas ARE the reason the player comes over.
// A prompt sharing that air would be covered by half the lines it exists to explain, so it sits
// below her instead, where a prone body leaves the ground clear and where a UI label reads as a
// label rather than as one more thing she is saying.
const CENTER_X = 16 // the entity's own centre line: the item is parked half a tile to its left
const TOP_Y = 58 // just clear of the soles (the item origin is a tile and a quarter above the hip)

const PAD_X = 7
const PAD_Y = 4
const RADIUS = 4
const FONT_SIZE = 10

// The breathing that makes a static box read as "do this". One slow cycle; the amplitude is small
// on purpose -- this is a label on a quiet beat after a fight, not an alarm.
const PULSE_MS = 1500
const PULSE_MIN_ALPHA = 0.74
const FADE_IN_MS = 220
// How long the ending takes. It is cut short rather than faded out when the hold begins, since the
// dial comes up in the same instant and two overlapping prompts about the same gesture is noise.
const FADE_OUT_MS = 120

const THEME = UI_THEMES.ember

export class RescueHint extends Container {
    private panel = new Graphics()
    private label: Text
    private startedAt = 0
    private endingAt?: number

    constructor(touch = isMobile()) {
        super()
        this.addChild(this.panel)
        this.label = new TextRenderer().fontSize(FONT_SIZE).bold().color(THEME.text)
            .render(touch ? "Touch and hold to help them up" : "Click and hold to help them up") as Text
        this.label.roundPixels = true
        // Pinned so the glyphs are not re-rasterized -- and so do not glitch for a frame -- when
        // this is rendered into the follow-cam PiP's RenderTexture at its own resolution.
        this.label.resolution = 2 * (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1)
        this.addChild(this.label)
        this.draw()
        this.visible = false
    }

    /** The server says the prompt belongs on screen. Idempotent: it arrives on every message. */
    show = (now = Date.now()) => {
        this.endingAt = undefined
        if (this.visible) {
            return
        }
        this.startedAt = now
        this.visible = true
        this.alpha = 0
    }

    /** The server has stopped asking for it -- the hold began, or she is up, or she is gone. */
    stop = (now = Date.now()) => {
        if (!this.visible || this.endingAt !== undefined) {
            return
        }
        this.endingAt = now
    }

    /** Whether this prompt still has something to draw. False once its ending has played out. */
    animate = (now = Date.now()): boolean => {
        if (!this.visible) {
            return false
        }
        if (this.endingAt !== undefined) {
            const t = Math.min(1, (now - this.endingAt) / FADE_OUT_MS)
            this.alpha = (1 - t) * PULSE_MIN_ALPHA
            if (t >= 1) {
                this.visible = false
                return false
            }
            return true
        }
        const elapsed = now - this.startedAt
        const pulse = PULSE_MIN_ALPHA + (1 - PULSE_MIN_ALPHA) * (0.5 - 0.5 * Math.cos((2 * Math.PI * elapsed) / PULSE_MS))
        this.alpha = elapsed < FADE_IN_MS ? (elapsed / FADE_IN_MS) * pulse : pulse
        return true
    }

    private draw = () => {
        const w = Math.round(this.label.width) + 2 * PAD_X
        const h = Math.round(this.label.height) + 2 * PAD_Y
        const x = CENTER_X - w / 2
        this.label.position.set(Math.round(x + PAD_X), Math.round(TOP_Y + PAD_Y))
        const g = this.panel
        g.clear()
        // A dark plate first, because this lands on whatever ground she is lying on.
        g.beginFill(0x000000, 0.35).drawRoundedRect(x, TOP_Y + 1.5, w, h, RADIUS).endFill()
        g.lineStyle(1, THEME.accent, 0.85).beginFill(THEME.fill, 0.94)
            .drawRoundedRect(x, TOP_Y, w, h, RADIUS).endFill()
    }
}
